import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { stringify } from 'qs';

/**
 * HTTP-клиент приложения.
 *
 * В браузере живёт один экземпляр на вкладку. На SSR-сервере экземпляр создаётся на каждый
 * HTTP-запрос и хранится в AsyncLocalStorage: так интерсепторы с токеном одного пользователя
 * не попадают в запросы другого и не накапливаются на общем модульном `axios`.
 *
 * API-классы должны вызывать `http(config)` вместо `axios(config)`.
 */

interface RequestHttpHolder {
  http: AxiosInstance;
}

/** Минимальный контракт AsyncLocalStorage, чтобы не тянуть типы Node в клиентский код */
interface RequestHttpStorage {
  getStore(): RequestHttpHolder | undefined;
  enterWith(store: RequestHttpHolder): void;
}

let serverStorage: RequestHttpStorage | null = null;

// Блок попадает только в серверный бандл: на клиенте webpack вырезает мёртвую ветку вместе с require,
// поэтому async_hooks не ищется в браузерной сборке
if (process.env.SERVER) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
  const { AsyncLocalStorage } = require('async_hooks');
  serverStorage = new AsyncLocalStorage();
}

let clientHttp: AxiosInstance | null = null;

export const createHttp = (): AxiosInstance =>
  axios.create({
    paramsSerializer: (params) =>
      stringify(params, {
        arrayFormat: 'repeat',
        skipNulls: true,
      }),
  });

/**
 * Возвращает экземпляр для текущего контекста выполнения.
 *
 * На сервере экземпляр должен быть заранее привязан к запросу через `acquireHttp()`
 * (boot-файл `axios` — до любого вызова `http()`, в том числе из cookie-auth).
 */
export const getHttp = (): AxiosInstance => {
  if (process.env.SERVER) {
    const holder = serverStorage?.getStore();
    if (!holder) {
      throw new Error(
        'HTTP-клиент не привязан к текущему SSR-запросу: ' +
          'вызов до boot-файла axios или вне контекста запроса'
      );
    }
    return holder.http;
  }

  if (!clientHttp) {
    clientHttp = createHttp();
  }
  return clientHttp;
};

/**
 * Выдаёт экземпляр для настройки интерсепторов в boot-файле.
 *
 * На сервере создаёт новый экземпляр и привязывает его к асинхронному контексту текущего запроса:
 * всё, что выполняется после этого в цепочке рендера (остальные boot, preFetch, setup), получит
 * его через `getHttp()`. В браузере возвращает общий экземпляр.
 */
export const acquireHttp = (): AxiosInstance => {
  if (process.env.SERVER && serverStorage) {
    const http = createHttp();
    serverStorage.enterWith({ http });
    return http;
  }
  return getHttp();
};

/** Замена прямому вызову `axios(config)` */
export const http = <T = any, R = AxiosResponse<T>, D = any>(
  config: AxiosRequestConfig<D>
): Promise<R> => getHttp().request<T, R, D>(config);

export type HttpRequest = typeof http;
